import { useMemo } from 'react';
import { useAuth, useOrganization, useUser } from '@clerk/clerk-react';
import type {
  PartnerAuthContext,
  PartnerPermission,
  PartnerRole,
  PartnerStatus,
  PartnerTier,
} from '@/types/auth';

const ALL_PERMISSIONS: PartnerPermission[] = [
  'org:profile:read',
  'org:profile:manage',
  'org:referrals:create',
  'org:referrals:read',
  'org:leads:manage',
  'org:payouts:read',
  'org:payouts:request',
  'org:team:read',
  'org:team:manage',
  'org:customers:read',
  'org:customers:contact',
  'org:support:chat',
  'org:analytics:read',
];

const ROLE_PERMISSIONS: Record<string, PartnerPermission[]> = {
  owner: ALL_PERMISSIONS,
  admin: ALL_PERMISSIONS,
  'org:admin': ALL_PERMISSIONS,
  finance: [
    'org:profile:read',
    'org:payouts:read',
    'org:payouts:request',
    'org:analytics:read',
    'org:team:read',
  ],
  marketing: [
    'org:profile:read',
    'org:referrals:create',
    'org:referrals:read',
    'org:leads:manage',
    'org:analytics:read',
    'org:support:chat',
  ],
  account_manager: [
    'org:profile:read',
    'org:referrals:read',
    'org:leads:manage',
    'org:customers:read',
    'org:support:chat',
  ],
  viewer: ['org:profile:read', 'org:referrals:read', 'org:analytics:read'],
  'org:member': ['org:profile:read', 'org:referrals:read', 'org:analytics:read'],
};

export function useAuthContext(): PartnerAuthContext {
  const { isLoaded: isAuthLoaded, isSignedIn, userId, orgId, orgRole, orgSlug, getToken } = useAuth();
  const { user, isLoaded: isUserLoaded } = useUser();
  const { organization, isLoaded: isOrgLoaded } = useOrganization();

  const isLoaded = isAuthLoaded && isUserLoaded && (isOrgLoaded || !orgId);

  // Normalize role string (strip "org:" prefix if needed or map directly)
  const resolvedRole = useMemo<PartnerRole | null>(() => {
    if (!orgRole) return null;
    return orgRole as PartnerRole;
  }, [orgRole]);

  // Compute permissions set
  const permissions = useMemo<Set<string>>(() => {
    const permSet = new Set<string>();

    if (!resolvedRole) return permSet;

    // Check predefined role mapping
    const baseRole = resolvedRole.replace(/^org:/, '').toLowerCase();
    const roleKey = ROLE_PERMISSIONS[resolvedRole] ? resolvedRole : baseRole;
    const defaultPerms = ROLE_PERMISSIONS[roleKey] || ROLE_PERMISSIONS['org:member'] || [];

    for (const perm of defaultPerms) {
      permSet.add(perm);
    }

    return permSet;
  }, [resolvedRole]);

  // Metadata claims
  const orgMetadata = (organization?.publicMetadata || {}) as Record<string, unknown>;
  const orgTier = (orgMetadata.tier as PartnerTier) || 'growth';
  const orgStatus = (orgMetadata.status as PartnerStatus) || 'active';
  const isSuspended = orgStatus === 'suspended' || Boolean(orgMetadata.isSuspended);

  const hasPermission = useMemo(
    () => (permission: PartnerPermission) => permissions.has(permission),
    [permissions]
  );

  const hasRole = useMemo(
    () => (roles: PartnerRole | PartnerRole[]) => {
      if (!resolvedRole) return false;
      const targetList = Array.isArray(roles) ? roles : [roles];
      const normalizedCurrent = resolvedRole.replace(/^org:/, '').toLowerCase();

      return targetList.some((r) => {
        const normalizedTarget = r.replace(/^org:/, '').toLowerCase();
        return normalizedTarget === normalizedCurrent || r === resolvedRole;
      });
    },
    [resolvedRole]
  );

  const hasTier = useMemo(
    () => (tier: PartnerTier | PartnerTier[]) => {
      const targetList = Array.isArray(tier) ? tier : [tier];
      return targetList.includes(orgTier);
    },
    [orgTier]
  );

  return {
    isLoaded,
    isSignedIn: Boolean(isSignedIn),
    userId: userId ?? null,
    user: user
      ? {
          id: user.id,
          fullName: user.fullName || user.username || user.firstName || 'Partner User',
          primaryEmail: user.primaryEmailAddress?.emailAddress ?? null,
          imageUrl: user.imageUrl ?? null,
        }
      : null,
    orgId: orgId ?? null,
    orgSlug: orgSlug ?? null,
    orgName: organization?.name ?? null,
    orgRole: resolvedRole,
    orgTier,
    orgStatus,
    permissions,
    hasPermission,
    hasRole,
    hasTier,
    isSuspended,
    getToken,
  };
}
